import React from "react";
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  Compass,
  Fish,
  MapPin,
  Plus,
  UsersRound,
  Waves,
} from "lucide-react";
import { WAY_OF_FISHING_OPTIONS } from "../../utils/catchMetadataOptions";
import {
  formatGroupDate,
  getGroupCoverScale,
  getGroupLocationText,
  getGroupMetadataItems,
  getGroupTypeOfFishingLabel,
  GROUP_SCOPE_FILTER_OPTIONS,
  GROUP_WATER_TYPE_OPTIONS,
  GroupStatusBadges,
  resolveGroupCoverObjectPosition,
  resolveGroupCoverUrl,
} from "./GroupsSharedUi";

const GROUP_METADATA_ICONS = {
  waterType: Waves,
  wayOfFishing: Compass,
  typeOfFishing: Fish,
};

const GROUPS_HERO_IMAGE = 'url("/banner/groups1.png")';
const GROUPS_HERO_DARK_IMAGE = 'url("/banner/Darkmode/groups-hero-dark.png")';

export default function GroupsDiscoveryView({ controller, t }) {
  return (
    <>
      <div className="groups-page-header groups-page-header-desktop">
        <div>
          <h2>{t("groups")}</h2>
          <p className="groups-page-subtitle">{t("groups_page_subtitle")}</p>
        </div>
        <button
          type="button"
          className="groups-create-toggle"
          onClick={controller.openCreateForm}
        >
          {t("groups_create_group")}
        </button>
      </div>

      <section
        className="groups-discovery-hero"
        style={{
          "--groups-hero-image": GROUPS_HERO_IMAGE,
          "--groups-hero-image-dark": GROUPS_HERO_DARK_IMAGE,
        }}
      >
        <div className="groups-discovery-hero-content">
          <h2>{t("groups_mobile_hero_title")}</h2>
          <p>{t("groups_mobile_hero_subtitle")}</p>
          <button
            type="button"
            className="groups-create-toggle groups-hero-create-button"
            onClick={controller.openCreateForm}
          >
            <span className="groups-hero-create-icon" aria-hidden="true">
              <Plus />
            </span>
            <span>{t("groups_create_group")}</span>
          </button>
        </div>
      </section>

      <div className="groups-filters-card">
        <div className="groups-filters-grid">
          <div className="groups-form-row groups-filter-field">
            <label htmlFor="groups-filter-scope">
              {t("groups_filter_scope", { defaultValue: "Group scope" })}
            </label>
            <div className="groups-filter-control">
              <UsersRound className="groups-filter-icon" aria-hidden="true" />
              <select
                id="groups-filter-scope"
                value={controller.groupFilterScope}
                onChange={(event) =>
                  controller.setGroupFilterScope(event.target.value)
                }
              >
                {GROUP_SCOPE_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey, { defaultValue: option.defaultLabel })}
                  </option>
                ))}
              </select>
              <ChevronDown className="groups-filter-chevron" aria-hidden="true" />
            </div>
          </div>

          <div className="groups-form-row groups-filter-field">
            <label htmlFor="groups-filter-water-type">
              {t("groups_water_type", { defaultValue: "Water type" })}
            </label>
            <div className="groups-filter-control">
              <Waves className="groups-filter-icon" aria-hidden="true" />
              <select
                id="groups-filter-water-type"
                value={controller.groupFilterWaterType}
                onChange={(event) =>
                  controller.setGroupFilterWaterType(event.target.value)
                }
              >
                {GROUP_WATER_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value === "all"
                      ? t("all", { defaultValue: option.defaultLabel })
                      : t(`groups_water_type_${option.value}`, {
                          defaultValue: option.defaultLabel,
                        })}
                  </option>
                ))}
              </select>
              <ChevronDown className="groups-filter-chevron" aria-hidden="true" />
            </div>
          </div>

          <div className="groups-form-row groups-filter-field">
            <label htmlFor="groups-filter-way-of-fishing">
              {t("catch_way_of_fishing", { defaultValue: "Way of Fishing" })}
            </label>
            <div className="groups-filter-control">
              <Compass className="groups-filter-icon" aria-hidden="true" />
              <select
                id="groups-filter-way-of-fishing"
                value={controller.groupFilterWayOfFishing}
                onChange={(event) =>
                  controller.handleGroupFilterWayOfFishingChange(event.target.value)
                }
              >
                <option value="all">{t("all", { defaultValue: "All" })}</option>
                {WAY_OF_FISHING_OPTIONS.map((way) => (
                  <option key={way} value={way}>
                    {way === "Shore"
                      ? t("fishing_way_shore", { defaultValue: way })
                      : way === "Boat"
                        ? t("fishing_way_boat", { defaultValue: way })
                        : way}
                  </option>
                ))}
              </select>
              <ChevronDown className="groups-filter-chevron" aria-hidden="true" />
            </div>
          </div>

          <div className="groups-form-row groups-filter-field">
            <label htmlFor="groups-filter-type-of-fishing">
              {t("catch_type_of_fishing", { defaultValue: "Type of Fishing" })}
            </label>
            <div className="groups-filter-control">
              <Fish className="groups-filter-icon" aria-hidden="true" />
              <select
                id="groups-filter-type-of-fishing"
                value={controller.groupFilterTypeOfFishing}
                onChange={(event) =>
                  controller.setGroupFilterTypeOfFishing(event.target.value)
                }
              >
                <option value="all">{t("all", { defaultValue: "All" })}</option>
                {controller.groupFilterTypeOfFishingOptions.map((type) => (
                  <option key={type} value={type}>
                    {getGroupTypeOfFishingLabel(t, type)}
                  </option>
                ))}
              </select>
              <ChevronDown className="groups-filter-chevron" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>

      {controller.loading ? (
        <div className="groups-status-card">{t("groups_loading")}</div>
      ) : controller.error ? (
        <div className="groups-status-card groups-status-error">
          {controller.error}
        </div>
      ) : controller.groups.length === 0 ? (
        <div className="groups-empty-state">
          <h3>{t("groups_empty_title")}</h3>
          <p>{t("groups_empty_subtitle")}</p>
        </div>
      ) : controller.filteredGroups.length === 0 ? (
        <div className="groups-empty-state">
          <h3>
            {t("groups_filters_empty_title", {
              defaultValue: "No groups match these filters.",
            })}
          </h3>
          <p>
            {t("groups_filters_empty_subtitle", {
              defaultValue: "Try broadening your water type or fishing filters.",
            })}
          </p>
        </div>
      ) : (
        <div className="groups-grid">
          {controller.filteredGroups.map((group) => {
            const memberCount = group.members.length;
            const cardCoverUrl = resolveGroupCoverUrl(group.coverImage);
            const cardLocationText = getGroupLocationText(group);
            const metadataItems = getGroupMetadataItems(group, t);

            return (
              <article key={group.id} className="groups-card">
                {cardCoverUrl ? (
                  <div className="groups-card-cover-wrap">
                    <img
                      src={cardCoverUrl}
                      alt={group.name}
                      className="groups-card-cover"
                      style={{
                        transform: `scale(${getGroupCoverScale(group)})`,
                        objectPosition: resolveGroupCoverObjectPosition(group),
                      }}
                    />
                  </div>
                ) : (
                  <div className="groups-card-cover-wrap groups-card-cover-placeholder">
                    <UsersRound aria-hidden="true" />
                  </div>
                )}

                <div className="groups-card-body">
                  <div className="groups-card-tags groups-card-tags-top">
                    <GroupStatusBadges
                      group={group}
                      controller={controller}
                      t={t}
                      compact
                    />
                  </div>

                  <div className="groups-card-header">
                    <h3 className="groups-card-title">{group.name}</h3>
                    {cardLocationText ? (
                      <p className="groups-card-location">
                        <MapPin aria-hidden="true" />
                        <span>{cardLocationText}</span>
                      </p>
                    ) : null}
                  </div>

                  <p className="groups-card-description">
                    {group.description || t("groups_no_description")}
                  </p>

                  {metadataItems.length > 0 ? (
                    <div className="groups-card-metadata">
                      {metadataItems.map((item) => {
                        const MetadataIcon = GROUP_METADATA_ICONS[item.key];

                        return (
                          <div
                            key={`${group.id}-${item.key}`}
                            className="groups-card-metadata-pill"
                          >
                            {MetadataIcon ? (
                              <MetadataIcon
                                className="groups-card-info-icon"
                                aria-hidden="true"
                              />
                            ) : null}
                            <span className="groups-card-metadata-label">
                              {item.label}
                            </span>
                            <strong className="groups-card-metadata-value">
                              {item.value}
                            </strong>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  <div className="groups-card-meta">
                    <div className="groups-card-meta-item">
                      <UsersRound
                        className="groups-card-info-icon"
                        aria-hidden="true"
                      />
                      <span>{t("groups_members_label")}</span>
                      <strong>
                        {memberCount}{" "}
                        {memberCount === 1
                          ? t("groups_member_count")
                          : t("groups_members")}
                      </strong>
                    </div>
                    <div className="groups-card-meta-item">
                      <CalendarDays
                        className="groups-card-info-icon"
                        aria-hidden="true"
                      />
                      <span>{t("groups_created")}</span>
                      <strong>{formatGroupDate(group.createdAt)}</strong>
                    </div>
                  </div>

                  <div className="groups-card-actions">
                    <button
                      type="button"
                      className="groups-action-btn groups-view-btn"
                      onClick={() => controller.openGroup(group.id)}
                    >
                      <span>{t("groups_view_group")}</span>
                      <ArrowRight aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </>
  );
}
